import * as zod from 'zod';

export const TeleopMode = zod.enum([
  'disabled',
  'ios',
  'headset',
  'hapticDevice',
]);
export type TeleopMode = zod.infer<typeof TeleopMode>;

export const TeleopFilterType = zod.enum(['identity', 'ema', 'smoothDamp']);
export type TeleopFilterType = zod.infer<typeof TeleopFilterType>;

export const TeleopRotationFrame = zod.enum(['TOOLTIP', 'WORLD']);
export type TeleopRotationFrame = zod.infer<typeof TeleopRotationFrame>;

/**
 * Per-robot teleop tunables. Stored as a JSONB blob on the
 * `teleop_settings.data` column so adding a new tunable doesn't require
 * a new column / migration. The outer row keeps `id`, `robotID`, and
 * `mode` as flat columns since those are queried on directly.
 */
const TeleopSettingsConfigBase = zod.object({
  arDevicePort: zod.number().int().min(1).max(65535),
  gripperVelocity: zod.number().positive(),
  flipXy: zod.boolean(),
  frequency: zod.number().positive(),

  /**
   * Per-robot calibration: compass heading (degrees clockwise from true
   * north) of the robot's gravity-aligned world +X axis. Required for iOS
   * teleop. Absent ⇒ uncalibrated; iOS teleop refuses to start. Set by the
   * iOS calibration screen via POST /api/v1/teleop/settings.
   */
  worldCompassHeadingDeg: zod.number().optional(),

  filterType: TeleopFilterType,

  emaSmoothingFactor: zod.number().min(0).max(1).optional(),

  smoothDampSmoothTime: zod.number().positive().optional(),
  smoothDampMaxAngularSpeed: zod.number().positive().optional(),
  smoothDampMaxSpeed: zod.number().positive().optional(),
  smoothDampMaxAccel: zod.number().positive().optional(),

  headPoseUdpHost: zod.string().optional(),
  headPoseUdpPort: zod.number().int().min(1).max(65535).optional(),

  hapticDevicePositionScale: zod.number().positive().optional(),
  hapticDeviceRotationScale: zod.number().positive().optional(),
  hapticDeviceRotationFrame: TeleopRotationFrame.optional(),
  hapticDeviceForceStiffnessK: zod.number().min(0).optional(),
  hapticDeviceForceDampingB: zod.number().min(0).optional(),

  gripperMin: zod.number().min(0).max(1).optional(),
  gripperMax: zod.number().min(0).max(1).optional(),

  /**
   * Haptic-device gripper button (button 2) behavior. When false (default)
   * the button is momentary: hold to close, release to open. When true it
   * latches — each press alternates the follower gripper between its
   * configured min (closed) and max (open) bounds.
   */
  hapticDeviceGripperButtonToggle: zod.boolean().optional(),
});

const gripperBoundsRefinement = {
  check: (data: { gripperMin?: number; gripperMax?: number }) =>
    data.gripperMin === undefined ||
    data.gripperMax === undefined ||
    data.gripperMin < data.gripperMax,
  options: {
    message: 'gripperMin must be strictly less than gripperMax',
    path: ['gripperMin'],
  },
};

export const TeleopSettingsConfig = TeleopSettingsConfigBase.refine(
  gripperBoundsRefinement.check,
  gripperBoundsRefinement.options,
);

export type TeleopSettingsConfig = zod.infer<typeof TeleopSettingsConfig>;

/**
 * Patch schema for partial updates: every field optional, but the
 * gripperMin/gripperMax invariant still applies when both are provided.
 * Used by the public REST API and feathers-bot's SetTeleopConfig RPC.
 */
export const TeleopSettingsConfigPatch =
  TeleopSettingsConfigBase.partial().refine(
    gripperBoundsRefinement.check,
    gripperBoundsRefinement.options,
  );

export type TeleopSettingsConfigPatch = zod.infer<
  typeof TeleopSettingsConfigPatch
>;

export const TeleopSettingsData = zod.object({
  id: zod.string(),
  robotID: zod.string(),
  mode: TeleopMode,
  data: TeleopSettingsConfig,
});

export type TeleopSettingsData = zod.infer<typeof TeleopSettingsData>;

export const TELEOP_SETTINGS_DEFAULTS: Omit<
  TeleopSettingsData,
  'id' | 'robotID'
> = {
  mode: 'disabled',
  data: {
    arDevicePort: 5153,
    gripperVelocity: 0.115,
    flipXy: false,
    frequency: 150.0,
    filterType: 'ema',
    emaSmoothingFactor: 0.08,
    smoothDampSmoothTime: 0.09,
    smoothDampMaxAngularSpeed: 10,
    smoothDampMaxSpeed: 0.2,
    smoothDampMaxAccel: 1.0,
    headPoseUdpHost: 'bolthead.local',
    headPoseUdpPort: 5005,
    hapticDevicePositionScale: 0.005,
    hapticDeviceRotationScale: 1.0,
    hapticDeviceRotationFrame: 'TOOLTIP',
    hapticDeviceForceStiffnessK: 25.0,
    hapticDeviceForceDampingB: 0.0,
    gripperMin: 0,
    gripperMax: 1,
    hapticDeviceGripperButtonToggle: false,
  },
};
